// The shared playhead across modes (workspace-modes note §4, §11): switching mode keeps the
// company, the subject (person, DE or seat), the scope and the week, and lands on the new mode's
// view of that subject when it has one, else on the mode's own default. One pure function, so the
// tabs, the keys, ⌘K and the phone bar all land in the same place, and the rule is tested.
import { type Focus, type FocusSearch, formatSubject } from "../focus/focus";
import type { ModeEntry } from "./registry";

/** Router navigation options for a mode switch (`to` is a typed route path). */
export type Landing = {
  to: string;
  params: Record<string, string>;
  search: FocusSearch;
};

/** The focus as query keys, exactly what a switch carries: never cleared, never invented. */
export function focusSearch(focus: Pick<Focus, "scope" | "period" | "subject" | "thread">) {
  const out: FocusSearch = {};
  if (focus.subject) out.s = formatSubject(focus.subject);
  if (focus.period) out.p = focus.period;
  if (focus.scope && focus.scope !== "company") out.scope = focus.scope;
  if (focus.thread) out.thread = focus.thread;
  return out;
}

/**
 * Where switching to `mode` lands for this focus. The subject's own view where the mode has one:
 * a DE opens its profile in Agents; a focused week opens that
 * week's Friday report. Every other combination lands on the mode's default view with the focus
 * carried in the query, so the chip keeps it and switching back restores it.
 */
export function modeLanding(
  mode: Pick<ModeEntry, "key" | "path">,
  focus: Pick<Focus, "company" | "scope" | "period" | "subject" | "thread">,
): Landing {
  const slug = focus.company.slug;
  // `thread` belongs to Chat's side panel: it rides into Chat only, never into another mode.
  const search = focusSearch({ ...focus, thread: mode.key === "chat" ? focus.thread : null });
  const de = focus.subject?.kind === "de" ? focus.subject.id : null;
  if (mode.key === "agents" && de)
    return { to: "/c/$slug/agents/de/$id", params: { slug, id: de }, search };
  if (mode.key === "reports" && focus.period)
    return { to: "/c/$slug/kpis/reports/$week", params: { slug, week: focus.period }, search };
  return { to: mode.path, params: { slug }, search };
}

/** The mode a `/c/{slug}/{mode}/…` path is in, or null outside a company mode. */
export function modeKeyOf(pathname: string): string | null {
  const [, c, slug, mode] = pathname.split("/");
  return c === "c" && slug && mode ? mode : null;
}
